import "./index.css";
import { useState } from "react";
import { SlouchDetector } from "./SlouchDetector";
import { HabitManager } from "./HabitManager";
import type { TrainingKind } from "./habitTypes";

type AppView = "manager" | "detection" | "training";

export function App() {
  const [view, setView] = useState<AppView>("manager");
  const [trainingHabitId, setTrainingHabitId] = useState<string | null>(null);
  const [trainingKind, setTrainingKind] = useState<TrainingKind | null>(null);

  const handleStartTraining = (habitId: string, kind: TrainingKind) => {
    setTrainingHabitId(habitId);
    setTrainingKind(kind);
    setView("training");
  };

  const handleStartDetection = () => {
    setView("detection");
  };

  const handleBackToManager = () => {
    setView("manager");
    setTrainingHabitId(null);
    setTrainingKind(null);
  };

  const handleTrainingComplete = (kind: TrainingKind) => {
    const message =
      kind === "neutral"
        ? "Baseline captured! We'll use this as 'normal' reference for the habit."
        : "Habit action captured! You're ready to compare against the baseline.";
    alert(message);
    handleBackToManager();
  };

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden text-white">
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-slate-900 via-slate-950 to-zinc-900" />
      <div className="absolute -z-10 inset-0 bg-[radial-gradient(circle_at_top,rgba(56,189,248,0.24),transparent_55%)]" />

      <main className="relative z-10 mx-auto flex max-w-6xl flex-col gap-16 px-6 pb-24 pt-16">
        {/* Header */}
        <header className="text-center">
          <h1 className="text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">
            Slouchy
          </h1>
          <p className="mt-3 text-lg text-white/70">
            AI-powered habit detection & training
          </p>
        </header>

        <section>
          {view === "manager" && (
            <HabitManager
              onStartTraining={handleStartTraining}
              onStartDetection={handleStartDetection}
            />
          )}
          
          {view === "detection" && (
            <div className="space-y-4">
              <button
                onClick={handleBackToManager}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white transition"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Habits
              </button>
              <SlouchDetector mode="detection" />
            </div>
          )}
          
          {view === "training" && trainingHabitId && trainingKind && (
            <SlouchDetector
              mode="training"
              trainingHabitId={trainingHabitId}
              trainingKind={trainingKind}
              onTrainingComplete={handleTrainingComplete}
              onBackToManager={handleBackToManager}
            />
          )}
        </section>
      </main>
    </div>
  );
}

export default App;
